/**
 * Class Schedules Tab — Master Data
 * Two sub-views:
 *   1. Schedules — CRUD cards
 *   2. Assignments — full grid: rows=classes grouped by class_group, columns=schedules, click to assign
 */

import {
  Box, Button, Typography, Card, CardContent, CardActions,
  Chip, CircularProgress, Alert, Tooltip, IconButton,
  Grid, Divider, Stack, Paper, Tab, Tabs,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Radio, TextField, InputAdornment,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  AccessTime as ClockIcon,
  CalendarToday as CalendarIcon,
  TableChart as ScheduleIcon,
  GridView as GridIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CalendarMonth as OverridesIcon,
} from '@mui/icons-material';
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getClassSchedulesDirect,
  deleteClassScheduleDirect,
  createClassScheduleDirect,
  getClassSectionsDirect,
  assignScheduleToClassesDirect,
} from '@timetable/api';
import type { ClassScheduleWithPeriods } from '@timetable/types';
import { ClassScheduleForm } from '../timetable/ClassScheduleForm';
import { ConfirmDialog } from '../ConfirmDialog';
import { queryKeys } from '../../lib/queryKeys';
import { ScheduleOverridesTab } from './ScheduleOverridesTab';

const DAY_LABELS: Record<string, string> = {
  MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed',
  THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun',
};
const DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const;
const fmt = (t: string) => t?.substring(0, 5) ?? '--:--';

interface Props { schoolId: string; }

// ── Root Tab ──────────────────────────────────────────────────────────────────

export function ClassSchedulesTab({ schoolId }: Props) {
  const [view, setView] = useState<'schedules' | 'assignments' | 'overrides'>('schedules');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Class Schedules</Typography>
          <Typography variant="body2" color="text.secondary">
            Define period schedules and assign them to classes
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={view}
        onChange={(_, v) => setView(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="schedules"
          label="Schedules"
          icon={<ScheduleIcon fontSize="small" />}
          iconPosition="start"
          sx={{ minHeight: 44 }}
        />
        <Tab
          value="assignments"
          label="Class Assignments"
          icon={<GridIcon fontSize="small" />}
          iconPosition="start"
          sx={{ minHeight: 44 }}
        />
        <Tab
          value="overrides"
          label="Overrides"
          icon={<OverridesIcon fontSize="small" />}
          iconPosition="start"
          sx={{ minHeight: 44 }}
        />
      </Tabs>

      {view === 'schedules' && <SchedulesView schoolId={schoolId} />}
      {view === 'assignments' && <AssignmentsGrid schoolId={schoolId} />}
      {view === 'overrides' && <ScheduleOverridesTab schoolId={schoolId} />}
    </Box>
  );
}

// ── Schedules View (CRUD cards) ───────────────────────────────────────────────

function SchedulesView({ schoolId }: { schoolId: string }) {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ClassScheduleWithPeriods | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ClassScheduleWithPeriods | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: schedules = [], isLoading } = useQuery({
    queryKey: queryKeys.classSchedules.list({ school_id: schoolId }),
    queryFn: () => getClassSchedulesDirect(schoolId),
    enabled: !!schoolId,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteClassScheduleDirect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classSchedules.all });
      setDeleteTarget(null);
    },
    onError: (err: Error) => { setDeleteError(err.message); setDeleteTarget(null); },
  });

  const duplicateMutation = useMutation({
    mutationFn: (s: ClassScheduleWithPeriods) =>
      createClassScheduleDirect({
        school_id: schoolId,
        name: `${s.name} (Copy)`,
        description: s.description ?? undefined,
        periods_count: s.periods_count,
        working_days: s.working_days,
        schedule_periods: s.schedule_periods.map(({ period_number, time_start, time_end, is_break, break_type, period_name, requires_supervision, supervision_type }) => ({
          period_number, time_start, time_end, is_break,
          break_type: break_type ?? null, period_name: period_name ?? null,
          requires_supervision, supervision_type: supervision_type ?? null,
        })),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.classSchedules.all }),
  });

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingSchedule(null); setFormOpen(true); }}>
          New Schedule
        </Button>
      </Box>

      {deleteError && <Alert severity="error" onClose={() => setDeleteError(null)} sx={{ mb: 2 }}>{deleteError}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : schedules.length === 0 ? (
        <Paper variant="outlined" sx={{ textAlign: 'center', py: 8, px: 4 }}>
          <ScheduleIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>No class schedules yet</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Create a schedule to define period timings, then assign it to classes in the "Class Assignments" tab.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingSchedule(null); setFormOpen(true); }}>
            Create First Schedule
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {schedules.map(s => (
            <Grid item xs={12} md={6} lg={4} key={s.id}>
              <ScheduleCard
                schedule={s}
                onEdit={() => { setEditingSchedule(s); setFormOpen(true); }}
                onDuplicate={() => duplicateMutation.mutate(s)}
                onDelete={() => setDeleteTarget(s)}
                duplicating={duplicateMutation.isPending}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <ClassScheduleForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingSchedule(null); }}
        schoolId={schoolId}
        schedule={editingSchedule}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Schedule"
        message={deleteTarget ? `Delete "${deleteTarget.name}"? Classes using it will lose their period schedule.` : ''}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}

// ── Schedule Card ─────────────────────────────────────────────────────────────

function ScheduleCard({ schedule, onEdit, onDuplicate, onDelete, duplicating }: {
  schedule: ClassScheduleWithPeriods;
  onEdit: () => void; onDuplicate: () => void; onDelete: () => void; duplicating: boolean;
}) {
  const teachPeriods = schedule.schedule_periods.filter(p => !p.is_break);
  const breaks = schedule.schedule_periods.filter(p => p.is_break);
  const sorted = [...schedule.schedule_periods].sort((a, b) => a.time_start.localeCompare(b.time_start));
  const dayStart = sorted[0]?.time_start;
  const dayEnd = sorted[sorted.length - 1]?.time_end;

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.15s', '&:hover': { borderColor: 'primary.main', boxShadow: 2 } }}>
      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap title={schedule.name}>{schedule.name}</Typography>
        {schedule.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{schedule.description}</Typography>
        )}
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ClockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {teachPeriods.length} periods{breaks.length > 0 ? `, ${breaks.length} break${breaks.length > 1 ? 's' : ''}` : ''}
            </Typography>
          </Box>
          {dayStart && dayEnd && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">{fmt(dayStart)} – {fmt(dayEnd)}</Typography>
            </Box>
          )}
        </Stack>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
          {DAY_ORDER.map(d => (
            <Chip key={d} label={DAY_LABELS[d]} size="small"
              color={schedule.working_days.includes(d) ? 'primary' : 'default'}
              variant={schedule.working_days.includes(d) ? 'filled' : 'outlined'}
              sx={{ opacity: schedule.working_days.includes(d) ? 1 : 0.25, fontSize: '0.65rem', height: 20 }}
            />
          ))}
        </Box>
        <PeriodTimeline periods={schedule.schedule_periods} />
      </CardContent>
      <Divider />
      <CardActions sx={{ px: 1.5, py: 1, justifyContent: 'flex-end' }}>
        <Tooltip title="Duplicate"><IconButton size="small" onClick={onDuplicate} disabled={duplicating}><DuplicateIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Edit"><IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={onDelete}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
      </CardActions>
    </Card>
  );
}

function PeriodTimeline({ periods }: { periods: ClassScheduleWithPeriods['schedule_periods'] }) {
  const sorted = [...periods].sort((a, b) => a.time_start.localeCompare(b.time_start));
  if (!sorted.length) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
      {sorted.map((p, i) => (
        <Tooltip key={i} title={`${p.period_name ?? (p.is_break ? 'Break' : `P${p.period_number}`)} · ${fmt(p.time_start)}–${fmt(p.time_end)}`}>
          <Box sx={{ height: 16, minWidth: p.is_break ? 8 : 20, borderRadius: 0.5, bgcolor: p.is_break ? 'warning.light' : 'primary.light', opacity: 0.85, cursor: 'default' }} />
        </Tooltip>
      ))}
    </Box>
  );
}

// ── Assignments Grid ──────────────────────────────────────────────────────────

function AssignmentsGrid({ schoolId }: { schoolId: string }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  // pending changes: classId → scheduleId | null
  const [pending, setPending] = useState<Map<string, string | null>>(new Map());
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const { data: schedules = [], isLoading: loadingSchedules } = useQuery({
    queryKey: queryKeys.classSchedules.list({ school_id: schoolId }),
    queryFn: () => getClassSchedulesDirect(schoolId),
    enabled: !!schoolId,
  });

  const { data: sections = [], isLoading: loadingSections } = useQuery({
    queryKey: queryKeys.classSections.list({ school_id: schoolId }),
    queryFn: () => getClassSectionsDirect(schoolId),
    enabled: !!schoolId,
  });

  // Unique classes with current schedule
  const classes = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; class_group: string; currentScheduleId: string | null }>();
    sections.forEach(s => {
      if (!seen.has(s.class_id)) {
        seen.set(s.class_id, {
          id: s.class_id,
          name: s.class.class_name,
          class_group: (s.class as any).class_group ?? 'Other',
          currentScheduleId: (s.class as any).class_schedule_id ?? null,
        });
      }
    });
    return Array.from(seen.values()).sort((a, b) => {
      const groupCmp = a.class_group.localeCompare(b.class_group);
      return groupCmp !== 0 ? groupCmp : a.name.localeCompare(b.name);
    });
  }, [sections]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q ? classes.filter(c => c.name.toLowerCase().includes(q) || c.class_group.toLowerCase().includes(q)) : classes;
  }, [classes, search]);

  // Effective schedule for a class (pending overrides saved)
  const effectiveSchedule = (classId: string): string | null => {
    if (pending.has(classId)) return pending.get(classId)!;
    return classes.find(c => c.id === classId)?.currentScheduleId ?? null;
  };

  const handleSelect = (classId: string, scheduleId: string | null) => {
    const saved = classes.find(c => c.id === classId)?.currentScheduleId ?? null;
    setPending(prev => {
      const next = new Map(prev);
      if (scheduleId === saved) {
        next.delete(classId);
      } else {
        next.set(classId, scheduleId);
      }
      return next;
    });
  };

  const pendingCount = pending.size;

  const handleSave = async () => {
    if (pending.size === 0) return;
    setSaving(true);
    try {
      const bySchedule = new Map<string | null, string[]>();
      pending.forEach((schedId, classId) => {
        if (!bySchedule.has(schedId)) bySchedule.set(schedId, []);
        bySchedule.get(schedId)!.push(classId);
      });

      for (const [schedId, classIds] of bySchedule.entries()) {
        if (schedId) {
          await assignScheduleToClassesDirect({ schedule_id: schedId, class_ids: classIds });
        }
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.classSchedules.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.classSections.all, refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: queryKeys.schedulePeriods.all, refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: queryKeys.classes.all });
      setPending(new Map());
      setToast({ msg: `Saved ${pendingCount} assignment${pendingCount > 1 ? 's' : ''}`, severity: 'success' });
    } catch (e: any) {
      setToast({ msg: e.message ?? 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => setPending(new Map());

  const isLoading = loadingSchedules || loadingSections;

  // Group filtered classes by class_group for section headers
  const byGroup = useMemo(() => {
    const map = new Map<string, typeof filtered>();
    filtered.forEach(c => {
      const g = c.class_group || 'Other';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(c);
    });
    return map;
  }, [filtered]);

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search classes…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Click a cell to assign a schedule to that class. Changes are highlighted until saved.
        </Typography>
        {pendingCount > 0 && (
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<CancelIcon />} onClick={handleDiscard} color="inherit" variant="outlined">
              Discard ({pendingCount})
            </Button>
            <Button
              size="small"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              variant="contained"
              disabled={saving}
            >
              Save Changes ({pendingCount})
            </Button>
          </Stack>
        )}
      </Box>

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: 'primary.main' }} />
          <Typography variant="caption" color="text.secondary">Assigned</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid', borderColor: 'warning.main', bgcolor: 'warning.50' }} />
          <Typography variant="caption" color="text.secondary">Pending change</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: '50%', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }} />
          <Typography variant="caption" color="text.secondary">Not assigned</Typography>
        </Box>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : schedules.length === 0 ? (
        <Alert severity="info">No schedules yet. Create schedules in the "Schedules" tab first.</Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No classes match your search.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: 'sticky', left: 0, zIndex: 3,
                    bgcolor: 'background.paper', fontWeight: 700,
                    minWidth: 160, borderRight: '2px solid', borderColor: 'divider',
                  }}
                >
                  Class
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 90, bgcolor: 'background.paper' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.3 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>None</Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', visibility: 'hidden' }}>–</Typography>
                  </Box>
                </TableCell>
                {schedules.map(s => (
                  <TableCell
                    key={s.id}
                    align="center"
                    sx={{ minWidth: 130, bgcolor: 'background.paper', '&.MuiTableCell-root': { verticalAlign: 'middle' } }}
                  >
                    <Tooltip title={
                      <Box>
                        <Typography variant="caption" fontWeight={700}>{s.name}</Typography>
                        {s.description && <Typography variant="caption" sx={{ display: 'block' }}>{s.description}</Typography>}
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {s.schedule_periods.filter(p => !p.is_break).length} periods · {s.working_days.map(d => DAY_LABELS[d]).join(', ')}
                        </Typography>
                      </Box>
                    } placement="top">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.3 }}>
                        <Typography variant="caption" fontWeight={700} noWrap sx={{ maxWidth: 120 }}>
                          {s.name}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                          {s.schedule_periods.filter(p => !p.is_break).length}p · {s.working_days.length}d
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {Array.from(byGroup.entries()).map(([group, groupClasses]) => (
                <>
                  {/* Group header row */}
                  <TableRow key={`group-${group}`}>
                    <TableCell
                      colSpan={schedules.length + 2}
                      sx={{
                        bgcolor: 'grey.50', py: 0.5, px: 2,
                        position: 'sticky', left: 0,
                        borderBottom: '1px solid', borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {group}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Class rows */}
                  {groupClasses.map(cls => {
                    const effective = effectiveSchedule(cls.id);
                    const isPending = pending.has(cls.id);

                    return (
                      <TableRow
                        key={cls.id}
                        sx={{
                          bgcolor: isPending ? 'warning.50' : 'inherit',
                          '&:hover': { bgcolor: isPending ? 'warning.100' : 'action.hover' },
                          transition: 'background-color 0.1s',
                        }}
                      >
                        <TableCell
                          sx={{
                            position: 'sticky', left: 0, zIndex: 1,
                            bgcolor: isPending ? 'warning.50' : 'background.paper',
                            borderRight: '2px solid', borderColor: 'divider',
                            fontWeight: 600,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{cls.name}</Typography>
                            {isPending && (
                              <Chip label="unsaved" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.6rem', height: 16 }} />
                            )}
                          </Box>
                        </TableCell>

                        <TableCell align="center" sx={{ cursor: 'pointer' }} onClick={() => handleSelect(cls.id, null)}>
                          <Radio
                            size="small"
                            checked={effective === null}
                            onChange={() => handleSelect(cls.id, null)}
                            sx={{ p: 0.5 }}
                          />
                        </TableCell>

                        {schedules.map(s => {
                          const isSelected = effective === s.id;
                          const isThisPending = isPending && pending.get(cls.id) === s.id;

                          return (
                            <TableCell
                              key={s.id}
                              align="center"
                              onClick={() => handleSelect(cls.id, s.id)}
                              sx={{
                                cursor: 'pointer',
                                bgcolor: isSelected
                                  ? isThisPending ? 'warning.100' : 'primary.50'
                                  : 'inherit',
                                '&:hover': { bgcolor: isSelected ? undefined : 'action.hover' },
                                transition: 'background-color 0.1s',
                              }}
                            >
                              <Radio
                                size="small"
                                checked={isSelected}
                                onChange={() => handleSelect(cls.id, s.id)}
                                color={isThisPending ? 'warning' : 'primary'}
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
